// The small confirmation that appears after a change and leaves on its own.
//
// It exists because the most important thing a journal does — removing an entry — looks exactly
// like deleting one. Saying "entry kept" at the moment the row disappears is what makes the
// difference visible, so it is announced politely rather than left as decoration.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconCheck } from '../components/Icons';

const LINGER_MS = 2400;

export function useToast() {
  const [toast, setToast] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ping = useCallback((message: string) => {
    setToast(message);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), LINGER_MS);
  }, []);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  return { toast, ping };
}

export function JournalToast({ text }: { text: string | null }) {
  if (!text) return null;
  // Portalled for the same reason the sheets are: the screen's scroller is its own stacking
  // context, and anything left inside it ends up under the navigation pill.
  return createPortal(
    <div className="jtoast" role="status" aria-live="polite">
      <IconCheck />
      {text}
    </div>,
    document.body,
  );
}
